import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, errorMessage, get, post } from '../../api.js';
import { useToast } from '../../components/Toast.js';
import { Card, Notice, PageHead, formatDateTime } from '../../components/ui.js';
import { useSignedIn } from '../../session.js';

interface Counts {
  synced: number;
  pending: number;
  error: number;
}

interface Status {
  connected: boolean;
  reachable: boolean;
  accountId: string | null;
  pipeline: string | null;
  counts: { contact: Counts; lead: Counts };
  failed: {
    kind: 'contact' | 'lead' | 'meeting';
    id: string;
    /** A meeting's first contact (Nov 3, 2026): its sheet lists the meeting. */
    contact_id?: string | null;
    error: string | null;
    attempts: number;
    next_attempt_at: string | null;
  }[];
  lastCheck: {
    ran_at: string;
    checked: number;
    mismatches: { kind: string; id: string; problem: string }[];
  } | null;
  /** Nov 2, 2026: the last Fill missing fields. */
  lastFill?: {
    requested_at: string;
    ran_at: string | null;
    contacts: number | null;
    deals: number | null;
    companies: number | null;
    error: string | null;
  } | null;
  /** Round 2, point C: what comes back from HubSpot. */
  fromHubSpot?: {
    lastRun: string | null;
    error: string | null;
    updatedToday: number;
    mergedThisMonth: number;
    deleted: {
      kind: 'contact' | 'lead';
      id: string;
      label: string | null;
      sub: string | null;
      deleted_at: string;
    }[];
  };
}

const PROBLEMS: Record<string, string> = {
  missing: 'not found in HubSpot',
  archived: 'archived in HubSpot',
  email: 'email changed in HubSpot',
  deal_name: 'deal name changed in HubSpot',
  unreachable: 'HubSpot did not answer',
};

/**
 * Settings > HubSpot (admins, admin.hubspot.manage; brief section 4): is
 * HubSpot connected, what was sent, what waits, what failed and why, the
 * last nightly check, and Send again. The key and the property names are
 * DigitalOcean variables, not settings of this page.
 */
export function HubSpotStatus() {
  const { refresh } = useSignedIn();
  const toast = useToast();
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let current = true;
    get<Status>('/admin/hubspot')
      .then((s) => current && setStatus(s))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
  }, [refresh, reload]);

  /** Retry one failed record, or all of them, without changing them. */
  async function retry(one?: { kind: 'contact' | 'lead' | 'meeting'; id: string }) {
    setBusy(true);
    try {
      const r = await post<{ contacts: number; leads: number }>('/admin/hubspot/retry', one ?? {});
      toast(
        one
          ? `Sending again: this ${one.kind}, in the next minute.`
          : `Sending again: ${r.contacts} contact(s), ${r.leads} lead(s).`,
      );
      setReload((n) => n + 1);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function fill() {
    setBusy(true);
    try {
      await post('/admin/hubspot/fill', {});
      toast('Asked: the records already in HubSpot get the fields they miss, in a few minutes.');
      setReload((n) => n + 1);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function restore(kind: 'contact' | 'lead', id: string, label: string) {
    setBusy(true);
    try {
      await post('/admin/hubspot/restore', { kind, id });
      toast(`Restored: ${label}. It goes to HubSpot again as a new record.`);
      setReload((n) => n + 1);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const people = (kind: 'contact' | 'lead', id: string) =>
    kind === 'contact' ? `/people/contacts/${id}` : `/people/leads/${id}`;

  return (
    <>
      <Link to="/settings" className="back">
        Settings
      </Link>
      <PageHead
        title="HubSpot"
        lede="Documented contacts and leads go to HubSpot by themselves, and what changes in HubSpot comes back. What failed is tried again with growing waits."
      />
      {error && <Notice kind="error">{error}</Notice>}
      {status && !status.connected && (
        <Notice kind="warn">
          HubSpot is not connected: its key is not set in DigitalOcean. Records wait in the portal
          and go once it is set.
        </Notice>
      )}
      {status?.connected && (
        <Notice kind={status.reachable ? 'ok' : 'warn'}>
          {status.reachable
            ? `Connected to HubSpot account ${status.accountId}, pipeline “${status.pipeline}”.`
            : 'HubSpot does not answer the portal right now, or refuses its key.'}
        </Notice>
      )}
      {status && (
        <Card title="Records">
          <dl className="kv">
            {(['contact', 'lead'] as const).map((k) => (
              <div key={k} style={{ display: 'contents' }}>
                <dt>{k === 'contact' ? 'Contacts' : 'Leads'}</dt>
                <dd>
                  {status.counts[k].synced} in HubSpot, {status.counts[k].pending} waiting,{' '}
                  {status.counts[k].error} failed
                </dd>
              </div>
            ))}
          </dl>
        </Card>
      )}
      {status?.connected && status.fromHubSpot && (
        <Card title="From HubSpot" note="Changes made in HubSpot come back every 5 minutes">
          <dl className="kv">
            <dt>Last reading</dt>
            <dd>
              {status.fromHubSpot.lastRun
                ? formatDateTime(status.fromHubSpot.lastRun)
                : 'Not run yet'}
              {status.fromHubSpot.error ? ` (${status.fromHubSpot.error}, tried again)` : ''}
            </dd>
            <dt>Updated from HubSpot</dt>
            <dd>{status.fromHubSpot.updatedToday} record(s) in the last 24 hours</dd>
            <dt>Merged in HubSpot</dt>
            <dd>{status.fromHubSpot.mergedThisMonth} record(s) followed in the last 30 days</dd>
          </dl>
        </Card>
      )}
      {status?.fromHubSpot && status.fromHubSpot.deleted.length > 0 && (
        <Card
          title="Deleted in HubSpot"
          note="Hidden in the portal; kept 30 days here, to restore one deleted by mistake"
        >
          <ul className="linked">
            {status.fromHubSpot.deleted.map((d) => {
              const label = d.label ?? (d.kind === 'contact' ? 'A contact' : 'A lead');
              return (
                <li key={`${d.kind}:${d.id}`}>
                  <span>
                    <b>{label}</b>
                    <br />
                    <span className="note">
                      {d.kind === 'contact' ? 'Contact' : 'Lead'}
                      {d.sub ? `, ${d.sub}` : ''}, deleted in HubSpot {formatDateTime(d.deleted_at)}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="btn sm"
                    disabled={busy}
                    aria-label={`Restore ${label}`}
                    onClick={() => void restore(d.kind, d.id, label)}
                  >
                    Restore
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
      {status && status.failed.length > 0 && (
        <Card title="Failed" note="The portal tries again by itself">
          <ul className="linked">
            {status.failed.map((f) => (
              <li key={`${f.kind}:${f.id}`}>
                <span>
                  <Link
                    to={
                      f.kind === 'meeting'
                        ? `/people/contacts/${f.contact_id ?? ''}`
                        : people(f.kind, f.id)
                    }
                  >
                    {f.kind === 'contact' ? 'Contact' : f.kind === 'lead' ? 'Lead' : 'Meeting'}
                  </Link>
                  <br />
                  <span className="note">
                    {f.error ?? 'No reason given'} {f.attempts} attempt(s)
                    {f.next_attempt_at ? `, next ${formatDateTime(f.next_attempt_at)}` : ''}.
                  </span>
                </span>
                <button
                  type="button"
                  className="btn sm"
                  disabled={busy}
                  aria-label={`Retry ${f.kind} ${f.id}`}
                  onClick={() => void retry({ kind: f.kind, id: f.id })}
                >
                  Retry
                </button>
              </li>
            ))}
          </ul>
          <div className="actions">
            <button
              type="button"
              className="btn primary"
              disabled={busy}
              onClick={() => void retry()}
            >
              Retry all
            </button>
          </div>
        </Card>
      )}
      {status && (
        <Card title="Nightly check" note="Records marked sent, compared with HubSpot">
          {!status.lastCheck ? (
            <p className="note">Not run yet. It runs every night.</p>
          ) : (
            <>
              <p style={{ margin: 0 }}>
                {formatDateTime(status.lastCheck.ran_at)}: {status.lastCheck.checked} checked,{' '}
                {status.lastCheck.mismatches.length} difference(s).
              </p>
              {status.lastCheck.mismatches.length > 0 && (
                <ul className="linked">
                  {status.lastCheck.mismatches.slice(0, 50).map((m) => (
                    <li key={`${m.kind}:${m.id}`}>
                      <span>
                        <Link to={people(m.kind === 'lead' ? 'lead' : 'contact', m.id)}>
                          {m.kind === 'lead' ? 'Lead' : 'Contact'}
                        </Link>{' '}
                        <span className="note">{PROBLEMS[m.problem] ?? m.problem}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </Card>
      )}
      {status?.connected && (
        <Card
          title="Fill missing fields"
          note="After a property is named in DigitalOcean (HUBSPOT_PROPERTY_MAP)"
        >
          <p style={{ margin: 0 }}>
            Contacts and leads already in HubSpot get the properties named since, only where HubSpot
            has them empty. Nothing filled in HubSpot is changed.
          </p>
          {status.lastFill && (
            <p className="note">
              {!status.lastFill.ran_at
                ? `Asked ${formatDateTime(status.lastFill.requested_at)}: running.`
                : status.lastFill.error
                  ? `${formatDateTime(status.lastFill.ran_at)}: ${status.lastFill.error}`
                  : `${formatDateTime(status.lastFill.ran_at)}: ${status.lastFill.contacts} contact(s), ${status.lastFill.deals} lead(s), ${status.lastFill.companies} company(ies) completed.`}
            </p>
          )}
          <div className="actions">
            <button
              type="button"
              className="btn"
              disabled={busy || (status.lastFill !== null && status.lastFill?.ran_at === null)}
              onClick={() => void fill()}
            >
              Fill missing fields
            </button>
          </div>
        </Card>
      )}
    </>
  );
}
